import { useEffect, useState } from 'react';
import type { DocumentIAResponse, IncomeProofIAResponse, ResidentProofIAResponse } from '../../../shared/models';
import type { UploadsResponse } from '../../../shared/models';
import type { CreateCustomerRequest, CreateProcessRequest } from '../../../shared/models';
import { formatCPF, formatPhone } from '../../../shared/utils';
import { ProcessRepository } from '../../../data/repositories';
import { CustomerRepository } from '../../../data/repositories';
import { HttpRepository } from '../../../data/repositories';

interface Step2Props {
  data: {
    nomeCompleto: string;
    cpf: string;
    dataNascimento: string;
    endereco: string;
    telefone: string;
  };
  onChange: (data: Partial<Step2Props['data']>) => void;
  onNext: () => void;
  onPrevious?: () => void;
  uploads: UploadsResponse;
}

const API_BASE_URL = 'https://integrador-n8n.grupo4u.com.br/webhook/c8b72bae-961c-480b-b18d-d09e0011d61a/7c78ba82-4bf4-4a43-81d8-8a5399862581';
const documentStatusRepository = new HttpRepository(API_BASE_URL);

export default function Step2PersonalInfo({ data, onChange, onNext, onPrevious, uploads }: Step2Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Função para extrair cidade e estado do endereço
  const extractAddressInfo = (address: string) => {
    // Padrão comum para endereços brasileiros
    const cityStatePattern = /,\s*([^,]+)\s*-\s*([A-Z]{2})/;
    const match = address.match(cityStatePattern);
    
    if (match) {
      return {
        city: match[1].trim(),
        state: match[2].trim(),
        zipcode: '00000-000' // Valor padrão
      };
    }
    
    // Fallback para endereços sem padrão específico
    return {
      city: 'São Paulo',
      state: 'SP',
      zipcode: '00000-000'
    };
  };

  const checkDocumentStatus = async (documentType: string, documentId: string): Promise<any> => {
    let endpoint = `/${documentType}/${documentId}`;
    
    try {
      const response = await documentStatusRepository.get(endpoint, {
        authenticated: false,
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Erro ao verificar status do documento ${documentType} (ID: ${documentId}):`, error);
      return { success: false, error: error instanceof Error ? error.message : 'Erro desconhecido' };
    }
  };

  // Função para extrair dados dos documentos e preencher o formulário
  const extractAndFillFormData = (
    documents: DocumentIAResponse,
    residenceProof: ResidentProofIAResponse,
    incomeProof: IncomeProofIAResponse
  ) => {
    const formData: Partial<Step2Props['data']> = {};

    // Tentar extrair nome completo dos documentos
    // Primeiro tenta do documento de identidade (RG/CPF)
    if (documents?.document_data?.identity_data?.full_name?.value) {
      formData.nomeCompleto = documents.document_data.identity_data.full_name.value;
    } 
    // Se não encontrar, tenta do comprovante de renda (certidão de nascimento)
    else if (incomeProof?.document_data?.birth_data?.civil_data?.registered_name?.value) {
      formData.nomeCompleto = incomeProof.document_data.birth_data.civil_data.registered_name.value;
    }

    // Tentar extrair CPF dos documentos
    // O CPF está na estrutura identity_data.cpf_number
    if (documents?.document_data?.identity_data?.cpf_number?.value) {
      formData.cpf = documents.document_data.identity_data.cpf_number.value;
    }

    // Tentar extrair data de nascimento dos documentos
    // Primeiro tenta do documento de identidade
    if (documents?.document_data?.identity_data?.birth_date?.value) {
      const birthDate = documents.document_data.identity_data.birth_date.value;
      if (birthDate) {
        try {
          const date = new Date(birthDate);
          if (!isNaN(date.getTime())) {
            formData.dataNascimento = date.toISOString().split('T')[0];
          }
        } catch (error) {
          console.warn('Erro ao converter data de nascimento:', error);
        }
      }
    } 
    // Se não encontrar, tenta do comprovante de renda (certidão de nascimento)
    else if (incomeProof?.document_data?.birth_data?.civil_data?.birth_date?.value) {
      const birthDate = incomeProof.document_data.birth_data.civil_data.birth_date.value;
      if (birthDate) {
        try {
          const date = new Date(birthDate);
          if (!isNaN(date.getTime())) {
            formData.dataNascimento = date.toISOString().split('T')[0];
          }
        } catch (error) {
          console.warn('Erro ao converter data de nascimento:', error);
        }
      }
    }

    // Tentar extrair endereço do comprovante de residência
    if (residenceProof?.residence_proof_data?.address?.value) {
      formData.endereco = residenceProof.residence_proof_data.address.value;
    }

    if (Object.keys(formData).length > 0) {
      onChange(formData);
    }
  };

  // Efeito para verificar status dos documentos quando o componente for montado
  useEffect(() => {
    const checkDocumentsOnMount = async () => {
      const [documents, residenceProof, incomeProof] = 
        await Promise.all(Object.keys(uploads).map(doc => checkDocumentStatus(doc, uploads[doc as keyof UploadsResponse].id)));
      
      const allRequestsSuccessful = [documents, residenceProof, incomeProof].every(
        response => response.success
      );
      
      if (allRequestsSuccessful) {
        // Fazer cast dos dados para os tipos corretos
        extractAndFillFormData(
          documents.data as DocumentIAResponse,
          residenceProof.data as ResidentProofIAResponse,
          incomeProof.data as IncomeProofIAResponse
        );
      } else {
        console.warn('Algumas requisições falharam. Verificando detalhes...');
        
        // Log detalhado de cada resposta para debug
        if (!documents.success) {
          console.error('Falha na requisição de documentos:', documents.error);
        }
        if (!residenceProof.success) {
          console.error('Falha na requisição de comprovante de residência:', residenceProof.error);
        }
        if (!incomeProof.success) {
          console.error('Falha na requisição de comprovante de renda:', incomeProof.error);
        }
        
        // Opcional: você pode mostrar uma mensagem para o usuário aqui
        // ou tentar novamente após um tempo
      }
    };

    checkDocumentsOnMount();
  }, []); // Array vazio significa que será executado apenas uma vez quando o componente for montado

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'telefone') {
      formattedValue = formatPhone(value);
    }

    // Garante que não sobrescrevemos os outros campos do formulário
    onChange({ [name]: formattedValue } as Partial<Step2Props['data']>);

    // Limpa o erro relativo ao campo assim que o usuário digita novamente
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!data.nomeCompleto.trim()) {
      newErrors.nomeCompleto = 'Nome completo é obrigatório';
    }

    if (!data.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (data.cpf.replace(/\D/g, '').length !== 11) {
      newErrors.cpf = 'CPF deve ter 11 dígitos';
    }

    if (!data.dataNascimento) {
      newErrors.dataNascimento = 'Data de nascimento é obrigatória';
    }

    if (!data.endereco.trim()) {
      newErrors.endereco = 'Endereço é obrigatório';
    }

    if (!data.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (data.telefone.replace(/\D/g, '').length < 10) {
      newErrors.telefone = 'Telefone deve ter pelo menos 10 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Extrair informações de cidade e estado do endereço
      const addressInfo = extractAddressInfo(data.endereco);
      
      // Criar o cliente primeiro
      const customerData: CreateCustomerRequest = {
        name: data.nomeCompleto,
        document_number: data.cpf.replace(/\D/g, ''), // Remove formatação do CPF
        birth_date: new Date(data.dataNascimento),
        address: data.endereco,
        city: addressInfo.city,
        state: addressInfo.state,
        zipcode: addressInfo.zipcode,
        phone: data.telefone.replace(/\D/g, ''), // Remove formatação do telefone
      };

      const customerRepository = new CustomerRepository();
      const createdCustomer = await customerRepository.create(customerData);

      if (!createdCustomer) {
        throw new Error('Erro ao criar cliente na base de dados');
      }

      // Criar o processo associado ao cliente
      const processData: CreateProcessRequest = {
        name: `Processo Habitação - ${data.nomeCompleto}`,
        customer_id: createdCustomer.id!,
        created_by: null,
        status: 'pending', // Status inicial do processo
      };

      const processRepository = new ProcessRepository();
      const createdProcess = await processRepository.create(processData);

      if (!createdProcess) {
        throw new Error('Erro ao criar processo na base de dados');
      }

      // Se tudo foi criado com sucesso, prosseguir para o próximo passo
      onNext();

    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      setSubmitError(error instanceof Error ? error.message : 'Erro desconhecido ao salvar dados');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-fraunces font-semibold text-textPrimary mb-2">
          Dados do Cliente
        </h2>
        <p className="font-instrument text-textSecondary">
          Preencha as informações do cliente para iniciar o processo
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nome completo */}
        <div>
          <label htmlFor="nomeCompleto" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
            Nome Completo do Cliente *
          </label>
          <input
            type="text"
            id="nomeCompleto"
            name="nomeCompleto"
            value={data.nomeCompleto}
            onChange={handleInputChange}
            className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
              errors.nomeCompleto ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
            }`}
            placeholder="Digite o nome completo do cliente"
          />
          {errors.nomeCompleto && (
            <p className="mt-1 text-sm text-red-600 font-instrument">{errors.nomeCompleto}</p>
          )}
        </div>

        {/* CPF e Data de Nascimento */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="cpf" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
              CPF do Cliente *
            </label>
            <input
              type="text"
              id="cpf"
              name="cpf"
              value={data.cpf}
              onChange={handleInputChange}
              maxLength={14}
              className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
                errors.cpf ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
              }`}
              placeholder="000.000.000-00"
            />
            {errors.cpf && (
              <p className="mt-1 text-sm text-red-600 font-instrument">{errors.cpf}</p>
            )}
          </div>

          <div>
            <label htmlFor="dataNascimento" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
              Data de Nascimento do Cliente *
            </label>
            <input
              type="date"
              id="dataNascimento"
              name="dataNascimento"
              value={data.dataNascimento}
              onChange={handleInputChange}
              className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
                errors.dataNascimento ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
              }`}
            />
            {errors.dataNascimento && (
              <p className="mt-1 text-sm text-red-600 font-instrument">{errors.dataNascimento}</p>
            )}
          </div>
        </div>

        {/* Endereço */}
        <div>
          <label htmlFor="endereco" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
            Endereço do Cliente *
          </label>
          <input
            type="text"
            id="endereco"
            name="endereco"
            value={data.endereco}
            onChange={handleInputChange}
            className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
              errors.endereco ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
            }`}
            placeholder="Rua, número, bairro, cidade, CEP"
          />
          {errors.endereco && (
            <p className="mt-1 text-sm text-red-600 font-instrument">{errors.endereco}</p>
          )}
        </div>

        {/* Telefone */}
        <div>
          <label htmlFor="telefone" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
            Telefone do Cliente *
          </label>
          <input
            type="tel"
            id="telefone"
            name="telefone"
            value={data.telefone}
            onChange={handleInputChange}
            maxLength={15}
            className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
              errors.telefone ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
            }`}
            placeholder="(00) 00000-0000"
          />
          {errors.telefone && (
            <p className="mt-1 text-sm text-red-600 font-instrument">{errors.telefone}</p>
          )}
        </div>

        {/* Botões de navegação */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6">
          {onPrevious && (
            <button
              type="button"
              onClick={onPrevious}
              disabled={isSubmitting}
              className="px-6 py-3 border border-gray-300 rounded-lg font-instrument font-medium text-textPrimary hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                <i className="ri-arrow-left-line"></i>
              </div>
              Voltar
            </button>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 rounded-lg font-instrument font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 inline-flex items-center justify-center mr-2 animate-spin">
                  <i className="ri-loader-4-line"></i>
                </div>
                Salvando...
              </>
            ) : (
              <>
                Próximo Passo
                <div className="w-4 h-4 inline-flex items-center justify-center ml-2">
                  <i className="ri-check-line"></i>
                </div>
              </>
            )}
          </button>
        </div>

        {/* Mensagem de erro */}
        {submitError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 font-instrument text-sm">
              <i className="ri-error-warning-line mr-2"></i>
              {submitError}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
